import React, { FC, JSX, memo } from 'react';
import { ThreeCircles } from 'react-loader-spinner';
import cn from 'classnames';

import { ToolTip } from 'components/atoms';
import { TriangleIcon } from 'components/icons';

import { ClassesModsType, StylesModsType } from '../index';

import './ItemsList.scss';

type ItemsListProps = {
  rows: Array<Array<string | number | JSX.Element>> | undefined;
  columns: Array<string>;
  sortBy: Array<string> | string;
  handleSort: (columnIndex: number) => void;
  sortedByColumn: { column: number | null; direction: number };
  searchedValue: string;
  rowClickFunc: (item: Array<string | number | JSX.Element>, index: number) => void;
  activeFilters?: boolean | undefined;
  loading?: boolean;
  stylesMods?: StylesModsType;
  classesMods?: ClassesModsType;
};

const ItemsList: FC<ItemsListProps> = ({
  rows,
  columns,
  sortBy,
  handleSort,
  sortedByColumn,
  searchedValue,
  rowClickFunc,
  activeFilters,
  loading,
  stylesMods,
  classesMods,
}) => {
  const renderSortArrow = (direction: number) => {
    switch (direction) {
      case -1:
        return <TriangleIcon className={'items-list__sort-icon revert'} />;
      case 1:
        return <TriangleIcon className={'items-list__sort-icon'} />;
      case 0:
        return <TriangleIcon className={'items-list__sort-icon transparent'} />;
      default:
        return <></>;
    }
  };

  return (
    <>
      <table className={cn('items-list')}>
        <thead>
          <tr className={'items-list__titles-row'}>
            {columns.map((item, index) => {
              let sortItem = false;
              if (rows?.length && typeof rows[0][index] !== 'object' && item) {
                if (sortBy === 'All') {
                  sortItem = true;
                } else if (typeof sortBy === 'object') {
                  sortItem = sortBy.includes(item);
                }
              }
              return (
                <ToolTip
                  placement={'top'}
                  arrow
                  title={
                    'Translation score is derived from multiple AI models that assess syntactic and semantic sentence structures.'
                  }
                  offset={[0, -30]}
                  tooltipStyles={{ display: item.includes('Score') ? 'block' : 'none' }}
                  key={index}
                >
                  <th
                    className={cn(
                      rows?.length &&
                        typeof rows[0][index] === 'object' &&
                        'items-list__jsx-content',
                    )}
                    style={{ cursor: sortItem ? (loading ? 'not-allowed' : 'pointer') : 'default' }}
                    onClick={() => sortItem && !loading && handleSort(index)}
                    scope={'col'}
                    key={index}
                  >
                    {item + ' '}
                    {sortedByColumn.column === index ? (
                      renderSortArrow(sortedByColumn.direction)
                    ) : (
                      <TriangleIcon className={'items-list__sort-icon transparent'} />
                    )}
                  </th>
                </ToolTip>
              );
            })}
          </tr>
        </thead>
        {rows && rows.length ? (
          <tbody>
            {rows.map((item, index) => (
              <tr
                onClick={() => rowClickFunc(item, index)}
                className={cn(
                  'items-list__row',
                  loading && 'items-list__row-loading',
                  classesMods?.find((mod) => mod.id === String(item[0]))?.className,
                )}
                key={index}
                style={stylesMods?.find((styleObj) => styleObj.id === String(item[0])) || {}}
              >
                {item.map((elem, indexI) => (
                  <td
                    className={cn(
                      rows?.length &&
                        typeof rows[index][indexI] === 'object' &&
                        'items-list__jsx-content-cell',
                    )}
                    key={indexI}
                  >
                    {typeof elem === 'object' ? elem : String(elem)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : (
          <></>
        )}
      </table>

      {!rows?.length ? (
        searchedValue ? (
          <div className={'items-list__no-matches'}>No matches found</div>
        ) : activeFilters || activeFilters === undefined ? (
          <div className={'items-list__no-matches'}>Empty list</div>
        ) : (
          <ThreeCircles
            visible={true}
            height="50"
            width="50"
            color="white"
            wrapperStyle={{}}
            wrapperClass=""
          />
        )
      ) : (
        <></>
      )}
    </>
  );
};

export default memo(ItemsList);
